import { LabMarker } from '@/components/labs/LabMarker'
import { getNavSequenceContinuation } from '@/components/sequences/navSequenceKeys'
import { useSequenceHintStore } from '@/components/sequences/sequenceHintStore'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { ShortcutHint } from '@/components/ui/shortcut-hint'
import {
	SidebarGroup,
	SidebarMenu,
	SidebarMenuButton,
	SidebarMenuItem,
	SidebarMenuSub,
	SidebarMenuSubButton,
	SidebarMenuSubItem,
} from '@/components/ui/sidebar'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { isLabId } from '@/config/labs'
import { getNavRouteTarget, resolveActualUrl } from '@/lib/navRoutes'
import { isOrganizationLevelRoute, stripScopePrefix } from '@/lib/routeScope'
import { Link, useLocation, useRouter, useSearch } from '@tanstack/react-router'
import { Settings } from 'lucide-react'

import { PlayFilledIcon } from '@/components/icons/PlayFilledIcon'
import { type NavItem, type NavSettingsOption, isUnderBasePath } from '@/data/nav-data'
import { useNavScope } from '@/hooks/useNavScope'
import { cn } from '@/lib/utils'
import type { ReactElement } from 'react'
import { useState } from 'react'

/** Nav item titles that render as a single link (no expandable children) in project nav */
const HIDE_CHILDREN_TITLES = new Set(['Device Management', 'Audit Logs', 'Sessions'])

type NavMainProps = {
	readonly items: NavItem[]
	/** When true, all items render as single links (e.g. settings, organization nav) */
	readonly hideAllChildren?: boolean
}

const getBasePath = (path: string) => {
	const normalizedPath = stripScopePrefix(path)
	const segments = normalizedPath.split('/').filter(Boolean)
	if (segments.length <= 1) {
		return normalizedPath
	}
	return `/${segments.slice(0, -1).join('/')}`
}

const shouldUseBasePathFallback = (path: string) => {
	const normalizedPath = stripScopePrefix(path)
	const segments = normalizedPath.split('/').filter(Boolean)
	return segments.length > 2
}

const isSectionPathActive = (path: string, currentPath: string) => {
	const normalizedCurrentPath = stripScopePrefix(currentPath)
	const normalizedNavPath = stripScopePrefix(path)

	if (isUnderBasePath(normalizedCurrentPath, normalizedNavPath)) {
		return true
	}

	// A tab strip stays lit while the URL sits on a sibling tab, but only for a path deep enough to have a
	// parent worth matching: `/device-management/all` would otherwise claim every module beside it.
	if (!shouldUseBasePathFallback(normalizedNavPath)) {
		return false
	}

	return isUnderBasePath(normalizedCurrentPath, getBasePath(normalizedNavPath))
}

// Settings tabs share a /settings prefix, so their section is 2 segments deep
// (/settings/users); 1 segment (/settings) would highlight every settings tab.
const getOrgSectionRoot = (normalizedPath: string) => {
	const segments = normalizedPath.split('/').filter(Boolean)
	const depth = segments[0] === 'settings' ? 2 : 1
	return `/${segments.slice(0, depth).join('/')}`
}

// relatedPaths[0] is the item's own URL; the rest are its settings options.
/** `?groups` belongs to the page that wrote it. Carried along, another page's grouped filter bar restores it
 *  as its own filter, against fields it does not have. */
export const navLinkSearch = (search: Record<string, unknown>): Record<string, unknown> => {
	const { groups: _, ...rest } = search
	return rest
}

export const isSingleLinkNavItemActive = (relatedPaths: string[], currentPath: string): boolean => {
	if (relatedPaths.some(path => isSectionPathActive(path, currentPath))) {
		return true
	}

	// Org sections resolve to a default tab (/meters → /meters/devices), so match the section
	// root to keep sibling tabs highlighted. Org-only; project nav keeps stricter per-item matching.
	const actualUrl = relatedPaths[0]
	if (!actualUrl || !isOrganizationLevelRoute(actualUrl)) {
		return false
	}
	return isUnderBasePath(stripScopePrefix(currentPath), getOrgSectionRoot(stripScopePrefix(actualUrl)))
}

const navLabMarker = (moduleKey: string | undefined) =>
	isLabId(moduleKey) && (
		<LabMarker data-testid={`nav-lab-marker-${moduleKey}`} className='ml-auto' side='right' focusable={false} />
	)

/**
 * Two independent things live here. Hovering or focusing an item, any time, shows a plain discovery
 * tooltip naming its full chord (`G then B`) — ordinary Radix hover/focus, nothing armed required.
 *
 * Separately, while a starter is armed and waiting on its second key, every reachable item shows its
 * own next key as a small badge sitting right on the item's own row — in the sidebar itself, not a
 * floating tooltip, so it reads as part of that row even when it has to sit on top of the label.
 * Appears after `HINT_APPEAR_DELAY_MS` (a fast, deliberate tap-tap chord should never see it flash on),
 * but disappears the instant the sequence is no longer armed for this item — see `sequenceHintStore`.
 */
function NavSequenceTooltip({
	itemId,
	label,
	trigger,
	nested = false,
}: {
	itemId: string
	label: string
	trigger: ReactElement
	/**
	 * A sub-item's own box already sits inset from the sidebar's true right edge, by `SidebarMenuSub`'s
	 * `mx-3.5` + `translate-x-px` + `px-1` (`src/components/ui/sidebar.tsx`) — about 17px net. This
	 * negative offset cancels that out so every badge, top-level or nested, lines up on the same edge.
	 */
	nested?: boolean
}) {
	const continuation = getNavSequenceContinuation(itemId)
	const armedKey = useSequenceHintStore(state => state.armedKey)
	const visibleArmedKey = useSequenceHintStore(state => state.visibleArmedKey)

	if (!continuation) {
		return trigger
	}

	// Instant to hide (armedKey clears straight away on cancel or completion), delayed to appear
	// (visibleArmedKey lags behind it) — see sequenceHintStore for why both exist.
	const showBadge = armedKey === continuation.starter && visibleArmedKey === continuation.starter

	return (
		<>
			<Tooltip>
				<TooltipTrigger asChild>{trigger}</TooltipTrigger>
				<TooltipContent side='right' sideOffset={16} hideArrow>
					<ShortcutHint label={label} keys={continuation.hint} />
				</TooltipContent>
			</Tooltip>
			{showBadge && (
				<span
					className={cn(
						'pointer-events-none absolute top-1/2 z-10 -translate-y-1/2 group-data-[collapsible=icon]/sidebar:hidden',
						nested ? '-right-3' : 'right-1.5',
					)}
					data-testid={`nav-sequence-hint-${itemId}`}>
					<ShortcutHint keys={continuation.key.toUpperCase()} surface='sidebar' />
				</span>
			)}
		</>
	)
}

function NavMain({ items, hideAllChildren = false }: NavMainProps) {
	const navScope = useNavScope()
	const shouldHideChildren = (item: NavItem) => hideAllChildren || HIDE_CHILDREN_TITLES.has(item.title)
	const getRouteParams = (itemPath: string) => getNavRouteTarget(itemPath, navScope)

	return (
		<SidebarGroup>
			<SidebarMenu>
				{items.map(item => (
					<NavItemComponent
						key={item.title}
						item={item}
						getRouteParams={getRouteParams}
						hideChildren={shouldHideChildren}
					/>
				))}
			</SidebarMenu>
		</SidebarGroup>
	)
}

function NavItemComponent({
	item,
	getRouteParams,
	hideChildren,
}: {
	item: NavItem
	getRouteParams: (path: string) => { to: string; params: Record<string, string> | undefined }
	hideChildren: (item: NavItem) => boolean
}) {
	const currentSearch = navLinkSearch(useSearch({ strict: false }))
	const router = useRouter()
	const location = useLocation()
	const currentPath = location.pathname
	const showSettingsInline = Boolean(item.hasSettings && item.settingsOptions?.length)
	const [settingItemOpen, setSettingItemOpen] = useState(false)

	const settingsOptionList = item.settingsOptions ?? []

	const navigateToSettingsOption = (option: NavSettingsOption) => {
		const routeInfo = getRouteParams(option.path)
		if (routeInfo.params) {
			router.navigate({ to: routeInfo.to, params: routeInfo.params })
		} else {
			router.navigate({ to: routeInfo.to })
		}
	}

	const renderSettingsDropdown = () => {
		if (!showSettingsInline) {
			return null
		}

		// A lone settings option navigates directly — no dropdown to open. The tooltip
		// names the destination so the gear isn't an unlabelled mystery icon.
		if (settingsOptionList.length === 1) {
			const option = settingsOptionList[0]
			return (
				<Tooltip>
					<TooltipTrigger asChild>
						<button
							type='button'
							aria-label={option.label}
							className='ml-1 opacity-0 cursor-pointer p-1 rounded-sm transition-opacity duration-200 group-hover/sidebar-btn:opacity-100 group-data-[collapsible=icon]/sidebar:hidden'
							onClick={e => {
								e.preventDefault()
								e.stopPropagation()
								navigateToSettingsOption(option)
							}}>
							<Settings className='size-(--icon-standalone)' />
						</button>
					</TooltipTrigger>
					<TooltipContent side='right' sideOffset={16} hideArrow>
						{option.label}
					</TooltipContent>
				</Tooltip>
			)
		}

		return (
			<DropdownMenu open={settingItemOpen} onOpenChange={setSettingItemOpen}>
				<DropdownMenuTrigger asChild>
					<div className='ml-1 opacity-0 cursor-pointer p-1 rounded-sm transition-opacity duration-200 group-hover/sidebar-btn:opacity-100 group-data-[collapsible=icon]/sidebar:hidden'>
						<Settings className='size-(--icon-standalone)' />
					</div>
				</DropdownMenuTrigger>
				<DropdownMenuContent side='left' align='start' className='space-y-2'>
					{item.settingsOptions?.map(option => {
						const routeInfo = getRouteParams(option.path)
						const actualUrl = resolveActualUrl(routeInfo)
						const isActive = isSectionPathActive(actualUrl, currentPath)

						return (
							<DropdownMenuItem
								key={option.label}
								className={cn(
									'gap-2 cursor-pointer text-body h-control! min-h-control!',
									isActive
										? 'bg-sidebar-accent text-header-tab-active-text font-medium hover:bg-sidebar-accent-hover focus:bg-sidebar-accent-hover'
										: 'text-header-tab-inactive-text hover:bg-sidebar-accent focus:bg-sidebar-accent hover:text-header-tab-active-text focus:text-header-tab-active-text',
								)}
								onClick={e => {
									e.preventDefault()
									e.stopPropagation() // Prevent collapsing
									if (routeInfo.params) {
										router.navigate({ to: routeInfo.to, params: routeInfo.params })
									} else {
										router.navigate({ to: routeInfo.to })
									}
									// close the dropdown
									setSettingItemOpen(false)
								}}>
								{option.icon && (
									<option.icon
										className={cn(
											'size-(--icon-standalone) shrink-0',
											isActive ? 'text-header-tab-active-text' : 'text-header-tab-inactive-text',
										)}
									/>
								)}
								{option.label}
							</DropdownMenuItem>
						)
					})}
				</DropdownMenuContent>
			</DropdownMenu>
		)
	}

	if (!item.items?.length || hideChildren(item)) {
		const routeInfo = getRouteParams(item.path)
		const actualUrl = resolveActualUrl(routeInfo)

		const relatedPaths = [
			actualUrl,
			...(item.settingsOptions?.map(option => resolveActualUrl(getRouteParams(option.path))) ?? []),
		]

		const isActive = isSingleLinkNavItemActive(relatedPaths, currentPath)
		return (
			<SidebarMenuItem>
				<div className='flex items-center group/sidebar-btn ml-1'>
					<NavSequenceTooltip
						itemId={item.id}
						label={item.title}
						trigger={
							<Link
								to={routeInfo.to}
								params={routeInfo.params}
								search={currentSearch}
								preload='intent'
								className='flex-1'>
								<SidebarMenuButton asChild isActive={isActive} className='flex-1'>
									<div className='flex items-center gap-1.5 flex-1'>
										{item.icon && (
											<item.icon
												className={cn(
													'size-(--icon-standalone) shrink-0',
													isActive
														? 'text-sidebar-active-text'
														: 'text-sidebar-inactive-text group-hover/sidebar-btn:text-sidebar-active-text',
												)}
											/>
										)}
										<span
											className={cn(
												'flex-1 text-body',
												isActive
													? 'text-sidebar-active-text'
													: 'text-sidebar-inactive-text group-hover/sidebar-btn:text-sidebar-active-text',
											)}>
											{item.title}
										</span>
										{navLabMarker(item.moduleKey)}
									</div>
								</SidebarMenuButton>
							</Link>
						}
					/>
					{renderSettingsDropdown()}
				</div>
			</SidebarMenuItem>
		)
	}

	return (
		<Collapsible asChild defaultOpen={true} className='group/collapsible'>
			<SidebarMenuItem>
				<div className='flex items-center group/sidebar-btn mb-1'>
					<CollapsibleTrigger asChild>
						<SidebarMenuButton className='flex'>
							<div className='flex items-baseline gap-2 justify-start flex-1'>
								<span className='align-middle text-body text-sidebar-inactive-text group-hover/sidebar-btn:text-sidebar-active-text'>
									{item.title}
								</span>
								<PlayFilledIcon
									className={cn(
										'size-2.5 shrink-0 align-middle transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90 text-sidebar-inactive-text group-hover/sidebar-btn:text-sidebar-active-text',
									)}
								/>
							</div>
						</SidebarMenuButton>
					</CollapsibleTrigger>
					<div className='ml-1 w-6 h-6 flex items-center justify-center'>{renderSettingsDropdown()}</div>
				</div>

				<CollapsibleContent>
					<SidebarMenuSub>
						{item.items.map(subItem => {
							const routeInfo = getRouteParams(subItem.path)
							const actualUrl = resolveActualUrl(routeInfo)

							const isActive = isSectionPathActive(actualUrl, currentPath)
							return (
								<SidebarMenuSubItem key={subItem.title}>
									<NavSequenceTooltip
										itemId={subItem.id}
										label={subItem.title}
										nested
										trigger={
											<SidebarMenuSubButton asChild isActive={isActive}>
												<Link
													to={routeInfo.to}
													params={routeInfo.params}
													search={currentSearch}
													preload='intent'
													className='flex items-center gap-1.5 px-3 ml-1'>
													{subItem.icon && (
														<subItem.icon
															className={cn(
																'size-(--icon-standalone) shrink-0',
																isActive
																	? 'text-sidebar-active-text!'
																	: 'text-sidebar-inactive-text! group-hover/menu-sub-item:text-sidebar-active-text!',
															)}
														/>
													)}
													<span
														className={cn(
															'text-body',
															isActive
																? 'text-sidebar-active-text'
																: 'text-sidebar-inactive-text group-hover/menu-sub-item:text-sidebar-active-text',
														)}>
														{subItem.title}
													</span>
													{navLabMarker(subItem.moduleKey)}
												</Link>
											</SidebarMenuSubButton>
										}
									/>
								</SidebarMenuSubItem>
							)
						})}
					</SidebarMenuSub>
				</CollapsibleContent>
			</SidebarMenuItem>
		</Collapsible>
	)
}

export { NavMain }
